import { Badge, type BadgeTone } from "../../../components/ui/Badge";
import type { WireStep, WireToken } from "../../../lib/api";
import { cn } from "../../../lib/cn";
import {
	formatAddress,
	formatTokenAmount,
	formatUsdcRaw,
	shortAssetId,
} from "../../../lib/format";
import { resolveSwapVenue } from "./swapVenue";

const STEP_TONE: Record<WireStep["kind"], BadgeTone> = {
	divest: "negative",
	invest: "positive",
	swap: "info",
};

/** Ordered on-chain plan list — used by SimulationBlock and TriageList. */
export function OnchainPlan({ steps }: { steps: WireStep[] }) {
	const groups = groupByBundle(steps);
	return (
		<div className="space-y-2">
			<p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
				On-chain plan (from simulated bundle)
			</p>
			<div className="space-y-3">
				{groups.map((g) => (
					<div key={g.bundleId} className="space-y-1">
						{groups.length > 1 && (
							<p className="font-mono text-[10px] uppercase tracking-wide text-slate-500">
								bundle {shortAssetId(g.bundleId, 14)}
							</p>
						)}
						<ol className="overflow-hidden rounded-lg border border-slate-800">
							{g.steps.map((step, i) => (
								<li
									key={step.stepId}
									className={cn(
										"flex items-center gap-2 px-3 py-2 text-xs",
										i !== g.steps.length - 1 && "border-b border-slate-800",
									)}
								>
									<span className="w-5 font-mono text-[10px] text-slate-500">
										{i + 1}.
									</span>
									<Badge tone={STEP_TONE[step.kind]}>{step.kind}</Badge>
									<StepLabel step={step} />
								</li>
							))}
						</ol>
					</div>
				))}
			</div>
		</div>
	);
}

function StepLabel({ step }: { step: WireStep }) {
	if (step.kind === "swap") {
		const amt = formatTokenAmount(step.amountInRaw, step.src.decimals);
		const out = formatTokenAmount(step.amountOutRaw, step.dst.decimals);
		const venue = resolveSwapVenue(step.router);
		return (
			<span
				className="flex flex-wrap items-baseline gap-x-2 font-mono text-slate-200"
				title={`${venue.name} · ${step.src.address} → ${step.dst.address}`}
			>
				<span className="inline-flex items-center gap-1 self-center">
					<img
						src={venue.iconUrl}
						alt={venue.name}
						title={`Routed via ${venue.name}`}
						className="h-4 w-4 rounded-sm"
					/>
					<span className="text-[10px] uppercase tracking-wide text-slate-400">
						{venue.name}
					</span>
				</span>
				<span>
					{amt} <span className="text-slate-500">{tokenLabel(step.src)}</span>
				</span>
				<span className="text-slate-500">→</span>
				<span>
					{out} <span className="text-slate-500">{tokenLabel(step.dst)}</span>
				</span>
				<span className="text-[10px] text-slate-500">
					slip {formatUsdcRaw(step.slippageUsdcRaw)}
				</span>
			</span>
		);
	}
	const token = step.kind === "divest" ? step.releasesToken : step.needsToken;
	const verb = step.kind === "divest" ? "releases" : "needs";
	const amount = formatTokenAmount(step.amountRaw, token.decimals);
	return (
		<span
			className="flex flex-wrap items-baseline gap-x-2 font-mono text-slate-200"
			title={`${step.protocol} · vault ${step.vault}`}
		>
			<span>{shortAssetId(step.assetId, 28)}</span>
			<span className="text-[10px] text-slate-500">{verb}</span>
			<span>
				{amount} <span className="text-slate-500">{tokenLabel(token)}</span>
			</span>
		</span>
	);
}

interface BundleGroup {
	bundleId: string;
	steps: WireStep[];
}

function groupByBundle(steps: WireStep[]): BundleGroup[] {
	const groups: BundleGroup[] = [];
	let current: BundleGroup | null = null;
	for (const step of steps) {
		if (!current || current.bundleId !== step.bundleId) {
			current = { bundleId: step.bundleId, steps: [] };
			groups.push(current);
		}
		current.steps.push(step);
	}
	return groups;
}

function tokenLabel(t: WireToken): string {
	return t.symbol ?? formatAddress(t.address);
}
